<template>
    <div class="relative h-full min-h-0 md:min-h-[92vh]">
        <div class="carousel relative h-full min-h-0 w-full overflow-hidden bg-[#1f2826] md:min-h-[92vh]">
            <div v-if="!hasImages" class="absolute inset-0 flex items-center justify-center">
                <div class="h-10 w-10 rounded-full border-2 border-white/25 border-t-white animate-spin"></div>
            </div>
            <div v-else class="carousel-images absolute inset-0 flex">
                <img v-for="(image, index) in images" :key="`${image}-${index}`" :src="image"
                    :class="{ 'opacity-100': index === currentIndex, 'opacity-0': index !== currentIndex }"
                    class="absolute inset-0 h-full w-full bg-[#1f2826] object-contain transition-opacity duration-1000 ease-in-out md:object-cover"
                    decoding="async" />
            </div>

        </div>

        <button
            v-if="canNavigate"
            class="absolute left-3 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#f7f2e8]/95 text-[#1f2826] shadow-[0_14px_35px_rgba(0,0,0,0.22)] ring-1 ring-white/70 transition hover:bg-white sm:left-6 sm:h-11 sm:w-11"
            type="button"
            aria-label="Previous carousel image"
            @click="previousSlide"
        >
            <i class="fas fa-chevron-left text-sm"></i>
        </button>

        <button
            v-if="canNavigate"
            class="absolute right-3 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#f7f2e8]/95 text-[#1f2826] shadow-[0_14px_35px_rgba(0,0,0,0.22)] ring-1 ring-white/70 transition hover:bg-white sm:right-6 sm:h-11 sm:w-11"
            type="button"
            aria-label="Next carousel image"
            @click="nextSlide"
        >
            <i class="fas fa-chevron-right text-sm"></i>
        </button>

        <div v-if="canNavigate"
            class="carousel-indicators absolute bottom-4 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-2 overflow-x-auto rounded-full bg-[#1f2826]/75 px-3 py-3 text-white shadow-[0_14px_35px_rgba(0,0,0,0.22)] ring-1 ring-white/40 backdrop-blur-md sm:gap-3 sm:px-4 md:bottom-24">
            <button
                v-for="(image, index) in images"
                :key="index"
                :class="index === currentIndex ? 'w-8 bg-[#f7f2e8]' : 'w-2.5 bg-white/60 hover:bg-white'"
                class="h-2.5 rounded-full transition-all duration-300"
                type="button"
                :aria-label="`Show carousel image ${index + 1}`"
                :aria-current="index === currentIndex ? 'true' : undefined"
                @click="goToSlide(index)"
            ></button>
        </div>
    </div>
</template>
  
<script>
import { computed, ref, watchEffect } from "vue";

export default {
    props: {
        images: Array,
    },
    setup(props) {
        const currentIndex = ref(0);
        const hasImages = computed(() => !!props.images?.length);
        const canNavigate = computed(() => !!props.images?.length && props.images.length > 1);
        const manualPauseUntil = ref(0);

        watchEffect((onInvalidate) => {
            if (!props.images || props.images.length <= 1) {
                currentIndex.value = 0;
                return;
            }

            const intervalId = setInterval(() => {
                if (Date.now() < manualPauseUntil.value) {
                    return;
                }

                currentIndex.value = (currentIndex.value + 1) % props.images.length;
            }, 4500);

            onInvalidate(() => {
                clearInterval(intervalId);
            });
        });

        const pauseAfterManualControl = () => {
            manualPauseUntil.value = Date.now() + 9000;
        };

        const goToSlide = (index) => {
            pauseAfterManualControl();
            currentIndex.value = index;
        };

        const previousSlide = () => {
            pauseAfterManualControl();
            currentIndex.value =
                (currentIndex.value - 1 + props.images.length) % props.images.length;
        };

        const nextSlide = () => {
            pauseAfterManualControl();
            currentIndex.value = (currentIndex.value + 1) % props.images.length;
        };

        return {
            canNavigate,
            currentIndex,
            goToSlide,
            hasImages,
            nextSlide,
            previousSlide,
        };
    },
};
</script>

  
<style scoped>
/* Add any additional SCSS styles here if needed */
</style>
  
